import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import axios from '../../../sharedModules/utils/axios.utils';

const useStyles = makeStyles(() => ({
  queryPreview: {
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  },
  fullQuery: {
    fontFamily: 'monospace',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflowX: 'auto',
  },
}));

interface ServmanSqlLog {
  id: number;
  query: string;
  category: string;
  created_at: string;
  updated_at: string;
  loggable_type: string;
  loggable_id: number;
  order_no: string | null;
}

interface ApiResponse {
  servman_sql_logs: ServmanSqlLog[];
}

const ServmanSqlLogsPage: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const [sqlLogs, setSqlLogs] = useState<ServmanSqlLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Determine the category based on the current route
  const isChillerModule = location.pathname.includes('/chiller_logs');
  const categoryTitle = isChillerModule ? 'Chiller Logs' : 'Backflow Testing';

  const fetchSqlLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get<ApiResponse>('/backflows/servman_sql_logs');
      setSqlLogs(response.data.servman_sql_logs || []);
    } catch (err: any) {
      console.error('Error fetching SQL logs:', err);
      setError(err.message || 'Failed to load SQL logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSqlLogs();
  }, []);

  const handleViewQuery = (query: string) => {
    setSelectedQuery(query);
    setOpenDialog(true);
  };

  const handleCopyQuery = (query: string) => {
    navigator.clipboard.writeText(query);
    // Could add a toast notification here
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQuery(null);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'backflow':
        return 'primary';
      case 'backflow_system_update':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const sqlLogsColumns = [
    {
      name: 'ID',
      selector: (row: any) => row.id,
      sortable: true,
      width: '80px',
    },
    {
      name: 'Category',
      selector: (row: any) => row.category,
      sortable: true,
      width: '180px',
      cell: (row: any) => <Chip label={row.category} color={getCategoryColor(row.category)} size="small" />,
    },
    {
      name: 'Order No',
      selector: (row: any) => row.order_no || 'N/A',
      sortable: true,
      width: '120px',
    },
    {
      name: 'SQL Query Preview',
      selector: (row: any) => row.query,
      sortable: false,
      grow: 2,
      cell: (row: any) => <Box className={classes.queryPreview}>{row.query}</Box>,
    },
    {
      name: 'Created At',
      selector: (row: any) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      width: '180px',
    },
    {
      name: 'Actions',
      width: '120px',
      cell: (row: any) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={() => handleViewQuery(row.query)} title="View Full Query">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="secondary" onClick={() => handleCopyQuery(row.query)} title="Copy Query">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle={categoryTitle} subTitle="Servman SQL Logs" />
      </Grid>
      <Grid item xs={12} style={{ marginTop: 20 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {categoryTitle} SQL Logs
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              This page displays all SQL statements generated for {categoryTitle.toLowerCase()} data synchronization
              with ServMan.
            </Typography>

            {error && (
              <Box mb={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}

            <DataTable
              columns={sqlLogsColumns}
              data={sqlLogs}
              progressPending={loading}
              pagination
              paginationPerPage={50}
              paginationRowsPerPageOptions={[25, 50, 100]}
              dense
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Full Query Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Full SQL Query</DialogTitle>
        <DialogContent>
          <Box className={classes.fullQuery}>{selectedQuery}</Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => selectedQuery && handleCopyQuery(selectedQuery)}>Copy to Clipboard</Button>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ServmanSqlLogsPage;
